import { defineStore } from 'pinia'
import { sliderToDollarValue, sliderToNumberValue, calculateSliderGradient } from '@/utils/formatters'

// Default question order
const DEFAULT_QUESTION_ORDER = [
  'amount',
  'income',
  'name',
  'email',
  'address',
  'dob',
  'phone'
]

export const useFormStore = defineStore('form', {
  state: () => ({
    // Question values
    values: {
      amount: 62,
      income: null,
      employed: null
    },

    // Name fields
    name: {
      first: '',
      last: ''
    },

    // Email fields
    email: {
      value: '',
      sendtoAC: true
    },

    // Phone fields
    phone: {
      value: '',
      unmasked: '',
      completed: false,
      tcpaLanguage: '',
      validationStatus: 'pending',
      overrideValidation: false
    },

    // Address fields
    address: {
      address: '',
      zip: '',
      city: '',
      state: '',
      overrideValidation: false
    },

    // DOB fields
    dob: {
      day: '',
      month: '',
      year: ''
    },

    // Navigation state
    questionOrder: [...DEFAULT_QUESTION_ORDER],
    currentProgress: 0,
    transitionDirection: 'forwards',
    removeQuestion: false,

    // UI state
    backButtonVisible: true,
    trackingDisclosureVisible: false,
    showSpinner: false,
    displayThankYou: false,

    // Array (credit bureau) data
    arrayData: {
      collections: null,
      credit_score: null,
      delinquencies: null,
      total_unsecured_debt: null,
      tax_liens: null,
      open_student_loan_bal: null
    }
  }),

  getters: {
    // Current question ID
    currentQuestionId: (state) => {
      return state.questionOrder[state.currentProgress]
    },

    // Progress percentage
    progressPercent: (state) => {
      return Math.round(((state.currentProgress + 1) / (state.questionOrder.length + 1)) * 100)
    },

    // Amount computed values
    amountDollarValue: (state) => {
      return sliderToDollarValue(state.values.amount)
    },

    amountNumberValue: (state) => {
      return sliderToNumberValue(state.values.amount)
    },

    amountGradient: (state) => {
      return calculateSliderGradient(state.values.amount, 0, 380)
    },

    // Validation getters
    isAmountValid: (state) => state.values.amount !== null,

    isIncomeValid: (state) => state.values.income !== null,

    isEmployedValid: (state) => state.values.employed !== null,

    isNameValid: (state) => {
      return !!(state.name.first && state.name.last)
    },

    isEmailValid: (state) => {
      return !!state.email.value
    },

    isPhoneValid: (state) => {
      if (state.phone.overrideValidation) return false
      return !!state.phone.value
    },

    isAddressValid: (state) => {
      if (state.address.overrideValidation) return false
      return !!(state.address.address && state.address.zip)
    },

    isDobValid: (state) => {
      return !!(state.dob.day && state.dob.month && state.dob.year)
    },

    // Check if location data is populated
    hasLocationData: (state) => {
      return !!(state.address.zip && state.address.city && state.address.state)
    },

    // Get name chat message (conditional based on debt/income)
    nameChatMessage: (state) => {
      const amt = sliderToNumberValue(state.values.amount)
      const inc = Number(state.values.income ?? 0)
      return (amt > 10000 && inc > 2000)
        ? '<p>Based on your answers, we could help you eliminate up to 50% of your debt!</p>'
        : ''
    },

    // Build complete lead data object for submission
    leadData: (state) => {
      return {
        amount: state.values.amount,
        income: state.values.income,
        employed: state.values.employed,
        firstName: state.name.first,
        lastName: state.name.last,
        email: state.email.value,
        phone: state.phone.unmasked,
        address: state.address.address,
        city: state.address.city,
        state: state.address.state,
        zip: state.address.zip,
        dobDay: state.dob.day,
        dobMonth: state.dob.month,
        dobYear: state.dob.year,
        arrayData: state.arrayData,
        tcpaLanguage: state.phone.tcpaLanguage,
        validationStatus: state.phone.validationStatus
      }
    }
  },

  actions: {
    // Load all values from localStorage
    loadFromStorage() {
      const params = new URLSearchParams(window.location.search)

      // Load amount
      const storedAmount = localStorage.getItem('amount')
      if (storedAmount) {
        this.values.amount = JSON.parse(storedAmount)
      }

      // Load income
      const storedIncome = localStorage.getItem('income')
      if (storedIncome) {
        this.values.income = JSON.parse(storedIncome)
      }

      // Load employed
      const storedEmployed = localStorage.getItem('employed')
      if (storedEmployed) {
        this.values.employed = JSON.parse(storedEmployed)
      }

      // Load name (with URL param override)
      const fnameParam = params.get('fname')
      const lnameParam = params.get('lname')
      this.name.first = (fnameParam && fnameParam !== '{fname}')
        ? fnameParam
        : localStorage.getItem('firstname') || ''
      this.name.last = (lnameParam && lnameParam !== '{lname}')
        ? lnameParam
        : localStorage.getItem('lastname') || ''

      // Load email (with URL param override)
      const emailParam = params.get('email')
      this.email.value = (emailParam && emailParam !== '{email}')
        ? emailParam
        : localStorage.getItem('email') || ''

      // Load phone (with URL param override)
      const phoneParam = this.cleanPhoneParam(params.get('phone'))
      this.phone.value = phoneParam || localStorage.getItem('phone') || ''
      if (phoneParam) {
        this.phone.unmasked = phoneParam.replace(/\D/g, '')
        this.phone.completed = this.phone.unmasked.length === 10
      }

      // Load address
      this.address.address = localStorage.getItem('address') || ''
      this.address.zip = localStorage.getItem('zip') || ''
      this.address.city = localStorage.getItem('city') || ''
      this.address.state = localStorage.getItem('state') || ''

      // Load DOB
      this.dob.day = localStorage.getItem('day') || ''
      this.dob.month = localStorage.getItem('month') || ''
      this.dob.year = localStorage.getItem('year') || ''

      // Load question order
      const storedOrder = localStorage.getItem('questionOrder')
      if (storedOrder) {
        this.questionOrder = JSON.parse(storedOrder)
      }
    },

    cleanPhoneParam(phone) {
      if (!phone) return null
      let cleaned = phone.replace(/-/g, '')
      if (cleaned.startsWith('1') || cleaned.startsWith('+1') || cleaned.startsWith(' 1')) {
        cleaned = cleaned.slice(-10)
      }
      return cleaned.length === 10 ? cleaned : null
    },

    // Set question order (used when array not requested)
    setQuestionOrder(order) {
      this.questionOrder = order
      localStorage.setItem('questionOrder', JSON.stringify(order))
    },

    // Set a simple value
    setValue(key, value) {
      if (this.values.hasOwnProperty(key)) {
        this.values[key] = value
        this.saveToStorage(key, value)
      }
    },

    // Set name
    setName(first, last) {
      this.name.first = first
      this.name.last = last
      localStorage.setItem('firstname', first)
      localStorage.setItem('lastname', last)
    },

    // Set email
    setEmail(value, sendtoAC = true) {
      this.email.value = value
      this.email.sendtoAC = sendtoAC
      localStorage.setItem('email', value)
    },

    // Set phone
    setPhone(value, tcpaLanguage = '', validationStatus = 'verified') {
      this.phone.value = value
      this.phone.unmasked = value ? value.replace(/\D/g, '') : ''
      this.phone.completed = this.phone.unmasked.length === 10
      this.phone.tcpaLanguage = tcpaLanguage
      this.phone.validationStatus = validationStatus
      localStorage.setItem('phone', value)
    },

    // Set phone TCPA language
    setPhoneTcpa(language) {
      this.phone.tcpaLanguage = language
    },

    // Set phone validation status
    setPhoneValidationStatus(status) {
      this.phone.validationStatus = status
    },

    // Set phone override validation
    setPhoneOverrideValidation(override) {
      this.phone.overrideValidation = override
    },

    // Set address
    setAddress(address, zip, city, state) {
      if (address !== undefined) this.address.address = address
      if (zip !== undefined) this.address.zip = zip
      if (city !== undefined) this.address.city = city
      if (state !== undefined) this.address.state = state

      if (address !== undefined) localStorage.setItem('address', address)
      if (zip !== undefined) localStorage.setItem('zip', zip)
      if (city !== undefined) localStorage.setItem('city', city)
      if (state !== undefined) localStorage.setItem('state', state)
    },

    // Set address override validation
    setAddressOverrideValidation(override) {
      this.address.overrideValidation = override
    },

    // Set DOB
    setDob(month, day, year) {
      this.dob.month = month
      this.dob.day = day
      this.dob.year = year
      localStorage.setItem('month', month)
      localStorage.setItem('day', day)
      localStorage.setItem('year', year)
    },

    // Set array data
    setArrayData(data) {
      for (const key in data) {
        if (this.arrayData.hasOwnProperty(key)) {
          this.arrayData[key] = data[key]
        }
      }
    },

    // Save a value to localStorage
    saveToStorage(key, value) {
      const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value)
      localStorage.setItem(key, stringValue)
    },

    // Navigation: proceed to next question
    proceed() {
      this.transitionDirection = 'forwards'
      this.removeQuestion = true

      setTimeout(() => {
        this.currentProgress++
        this.removeQuestion = false

        // Scroll to top
        document.getElementById('header')?.scrollIntoView()

        // Update URL hash
        const currentId = this.questionOrder[this.currentProgress]
        history.pushState({ currentProgress: this.currentProgress }, '', '#' + currentId)

        // Save question order
        localStorage.setItem('questionOrder', JSON.stringify(this.questionOrder))
      }, 10)
    },

    // Navigation: go back
    goBack() {
      history.back()
    },

    // Handle browser back/forward
    handlePopState(event) {
      if (event.state === null) {
        window.history.go(-1)
      } else {
        this.transitionDirection = this.currentProgress < event.state.currentProgress ? 'forwards' : 'backwards'
        this.removeQuestion = true

        setTimeout(() => {
          this.currentProgress = event.state.currentProgress
          this.removeQuestion = false
          document.getElementById('header')?.scrollIntoView()
          localStorage.setItem('questionOrder', JSON.stringify(this.questionOrder))
        }, 10)
      }
    },

    // Set current progress (used when restoring from URL hash)
    setCurrentProgress(progress) {
      this.currentProgress = progress
    },

    // UI: hide back button
    hideBackButton() {
      this.backButtonVisible = false
    },

    // UI: show back button
    showBackButton() {
      this.backButtonVisible = true
    },

    // UI: show tracking disclosure
    showTrackingDisclosure() {
      this.trackingDisclosureVisible = true
    },

    // UI: hide tracking disclosure
    hideTrackingDisclosure() {
      this.trackingDisclosureVisible = false
    },

    // UI: show spinner
    showSpinnerUI() {
      this.showSpinner = true
    },

    // UI: show thank you
    showThankYou() {
      this.displayThankYou = true
    },

    // Check if a specific question is valid
    isQuestionValid(questionId) {
      switch (questionId) {
        case 'amount':
          return this.values.amount !== null
        case 'income':
          return this.values.income !== null
        case 'employed':
          return this.values.employed !== null
        case 'name':
          return !!(this.name.first && this.name.last)
        case 'email':
          return !!this.email.value
        case 'phone':
          return !!this.phone.value
        case 'address':
          return !!(this.address.address && this.address.zip)
        case 'dob':
          return !!(this.dob.day && this.dob.month && this.dob.year)
        default:
          return this.values[questionId] !== null && this.values[questionId] !== undefined
      }
    },

    // Check if all questions up to a point have valid data
    validateProgressTo(targetProgress) {
      for (let i = 0; i < targetProgress; i++) {
        const questionId = this.questionOrder[i]

        // Special handling for name (has multiple fields)
        if (questionId === 'name') {
          if (!this.name.first || !this.name.last) {
            return i
          }
          continue
        }

        // Check if question value exists
        if (this.values.hasOwnProperty(questionId)) {
          if (this.values[questionId] === null || this.values[questionId] === '') {
            return i
          }
        }
      }

      return targetProgress
    }
  }
})
